import {
	DEFAULT_PORTAL_MOBILE_NAV_TABS,
	type PortalSiteDisplaySettingsDto,
} from '@supply-mover-2-0/shared';
import { useMemo } from 'react';
import { usePortalAuthStore } from '../stores/portalAuthStore';
import { usePortalSiteStore } from '../stores/portalSiteStore';

/**
 * Effective storefront display flags for the current visitor.
 *
 * Authenticated /auth/me supersedes the public bootstrap when present.
 * Falls back to the bootstrap (anonymous-visitor flags) and finally to a
 * permissive logged-in default so logic that consults this hook before
 * either response lands doesn't render an empty product card.
 *
 * IMPORTANT: These are UI hints. The API independently strips fields
 * (price, contractPrice, inStock) from responses based on the same
 * underlying portal_site.settings — never rely on this hook for security.
 */
const DEFAULT_AUTH_FALLBACK: PortalSiteDisplaySettingsDto = {
	isAnonymousVisitor: false,
	canSeePricing: true,
	canSeeCatalogPricing: true,
	canSeeStockAvailability: true,
	canSeeStockQuantity: false,
	stockDisplayMode: 'availability',
	canBrowseCatalog: true,
	canPurchase: true,
	// PD-13362: only the server knows the account is a lead; until it says
	// so, checkout behaves as it does for a customer (the API refuses a
	// lead's submit either way).
	isAccountPending: false,
	// Matches the API default: no Schedule II until the server says the
	// storefront sells it (PD-16776).
	canOrderScheduleII: false,
	canViewOrderHistory: true,
	showShipVia: true,
	canViewInvoices: true,
	showAvailableCredit: true,
	showPaymentTerms: true,
	// Opt-in product identifier lines (PD-16126) stay hidden until the server
	// says the storefront shows them.
	showItemNdc: false,
	showItemSize: false,
	canViewStatements: true,
	canViewTransactions: true,
	canPayInvoices: true,
	// Conservative: chat surfaces stay hidden until the server confirms
	// the portal has opted in (PD-14608).
	canUseChat: false,
	mobileNavTabs: DEFAULT_PORTAL_MOBILE_NAV_TABS,
	// Match the API default (`portal-site-context.service.ts`) — landing
	// authenticated users on the dashboard is the campaign-promoted
	// home. Tenants that prefer browse-first opt back via the portal_site
	// row.
	defaultAuthenticatedView: 'dashboard',
};

/**
 * Back-compat for API servers that predate newer displaySettings flags, so a
 * new app bundle pointed at a not-yet-redeployed API doesn't silently regress:
 *   - `canViewStatements` / `canViewTransactions` (PD-14675): older servers
 *     omit these; both surfaces used to piggyback on the invoices flag, so a
 *     missing key falls back to `canViewInvoices` — NOT to hidden.
 *   - `showShipVia` (PD-14771): older servers omit this; the Ship Via row was
 *     always shown before the toggle existed, so a missing key falls back to
 *     `true` — NOT to hidden.
 *   - `canSeeCatalogPricing` (PD-14775): older servers omit this; catalog
 *     prices used to be gated by the general pricing flag, so a missing key
 *     falls back to `canSeePricing` — NOT to hidden.
 *   - `showAvailableCredit` (PD-14899): older servers omit this; the metric
 *     was always shown before the toggle existed, so a missing key falls
 *     back to `true` — NOT to hidden.
 *   - `showPaymentTerms` (PD-14898): older servers omit this; the invoice
 *     Terms line and the statement Payment Terms tile were always shown
 *     before the toggle existed, so a missing key falls back to `true` —
 *     NOT to hidden.
 *   - `showItemNdc` / `showItemSize` (PD-16126): older
 *     servers omit these. Unlike the flags above they are opt-in lines that
 *     never existed before, so a missing key falls back to `false` — hidden,
 *     which is exactly what the storefront showed before the toggles.
 *   - `stockDisplayMode` (PD-16750): older servers omit this and only ever
 *     sent an exact number, so a missing key is read from the two stock
 *     flags: exact when a quantity shows, else availability or hidden.
 *   - `canOrderScheduleII` (PD-16776): older servers omit this and never
 *     sold C2 or C2N, so a missing key falls back to `false`, matching what
 *     those servers enforce.
 *   - `isAccountPending` (PD-13362): older servers omit this and never
 *     refused a lead's order, so a missing key falls back to `false`.
 */
function withDisplayFlagFallbacks(
	display: PortalSiteDisplaySettingsDto,
): PortalSiteDisplaySettingsDto {
	// The DTO types these as required booleans, but the runtime payload
	// from an older server can lack them — read through Partial to keep
	// the null checks honest.
	const raw: Partial<PortalSiteDisplaySettingsDto> = display;
	if (
		raw.canViewStatements != null &&
		raw.canViewTransactions != null &&
		raw.showShipVia != null &&
		raw.canSeeCatalogPricing != null &&
		raw.showAvailableCredit != null &&
		raw.showPaymentTerms != null &&
		raw.showItemNdc != null &&
		raw.showItemSize != null &&
		raw.stockDisplayMode != null &&
		raw.canOrderScheduleII != null &&
		raw.isAccountPending != null
	) {
		return display;
	}
	return {
		...display,
		canViewStatements: raw.canViewStatements ?? display.canViewInvoices,
		canViewTransactions: raw.canViewTransactions ?? display.canViewInvoices,
		showShipVia: raw.showShipVia ?? true,
		canSeeCatalogPricing: raw.canSeeCatalogPricing ?? display.canSeePricing,
		showAvailableCredit: raw.showAvailableCredit ?? true,
		showPaymentTerms: raw.showPaymentTerms ?? true,
		showItemNdc: raw.showItemNdc ?? false,
		showItemSize: raw.showItemSize ?? false,
		canOrderScheduleII: raw.canOrderScheduleII ?? false,
		isAccountPending: raw.isAccountPending ?? false,
		stockDisplayMode:
			raw.stockDisplayMode ??
			(display.canSeeStockQuantity
				? 'exact'
				: display.canSeeStockAvailability
					? 'availability'
					: 'hidden'),
	};
}

export function useDisplaySettings(): PortalSiteDisplaySettingsDto {
	const authDisplay = usePortalAuthStore((s) => s.displaySettings);
	const isAuthenticated = usePortalAuthStore((s) => s.isAuthenticated);
	const bootstrapDisplay = usePortalSiteStore(
		(s) => s.site?.displaySettings ?? null,
	);

	// Memoized so consumers that put the whole object in a dep array
	// (e.g. StorefrontDashboard's widget memo) keep a stable reference —
	// the fallback path builds a new object.
	return useMemo(() => {
		if (isAuthenticated && authDisplay != null) {
			return withDisplayFlagFallbacks(authDisplay);
		}

		if (bootstrapDisplay != null) {
			return withDisplayFlagFallbacks(bootstrapDisplay);
		}

		return DEFAULT_AUTH_FALLBACK;
	}, [isAuthenticated, authDisplay, bootstrapDisplay]);
}
